import { FileText, Lock, Trash2, ShieldCheck } from 'lucide-react';
import { useInstanceEvidence } from '../hooks/useOperator';
import { desktopDeepLinks } from '../lib/deep-links';
import { DeepLinkButton } from './operator-ui';
import type { EvidenceRetentionClass } from '../types/operator';

/**
 * WI-14 (OPERATOR-UI-PLACEMENT): read-only evidence ledger for a selected
 * attempt. Renders retention class, retention window, legal hold, and tombstone
 * state from GET /api/instances/:id/evidence. Never mutates — retention actions
 * hand off to the native Aldan Desktop console via a typed deep link.
 */

function retentionClasses(cls: EvidenceRetentionClass | null): string {
  switch (cls) {
    case 'legal':
      return 'bg-red-50 text-red-600';
    case 'extended':
      return 'bg-amber-50 text-amber-600';
    case 'standard':
      return 'bg-slate-100 text-slate-600';
    case 'transient':
      return 'bg-slate-50 text-slate-400';
    default:
      return 'bg-slate-50 text-slate-400';
  }
}

export default function EvidencePanel({ instanceId }: { instanceId: string }) {
  const { data, isLoading } = useInstanceEvidence(instanceId);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Evidence ledger</h2>
          {data && (
            <span className="text-xs text-slate-400">
              {data.evidence.length} of {data.total}
            </span>
          )}
        </div>
        <DeepLinkButton href={desktopDeepLinks.evidence(instanceId)} label="Open in Desktop" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Event</th>
              <th className="px-3 py-2 font-medium">Retention</th>
              <th className="px-3 py-2 font-medium">Until</th>
              <th className="px-3 py-2 font-medium">State</th>
              <th className="px-3 py-2 font-medium">Recorded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                  Loading evidence…
                </td>
              </tr>
            )}
            {!isLoading && (data?.evidence.length ?? 0) === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                  No evidence recorded
                </td>
              </tr>
            )}
            {data?.evidence.map((e) => (
              <tr key={e.id} className="text-slate-700">
                <td className="px-3 py-2 font-mono text-xs">{e.eventType}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${retentionClasses(
                      e.retentionClass,
                    )}`}
                  >
                    {e.retentionClass ?? 'unset'}
                  </span>
                  {e.retentionPolicyRef && (
                    <span className="ml-1.5 font-mono text-[11px] text-slate-400">
                      {e.retentionPolicyRef}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {e.retentionUntil
                    ? new Date(e.retentionUntil).toLocaleString()
                    : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {e.legalHold && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
                        <Lock className="h-3 w-3" />
                        Legal hold
                      </span>
                    )}
                    {e.tombstonedAt ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                        <Trash2 className="h-3 w-3" />
                        Tombstoned
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <ShieldCheck className="h-3 w-3" />
                        Retained
                      </span>
                    )}
                    {e.deletionReceiptRef && (
                      <span className="font-mono text-[11px] text-slate-400">
                        {e.deletionReceiptRef}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {new Date(e.createdAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
